import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import { Button, Input } from 'semantic-ui-react';
import styles from './TaskAssigneeCard.module.css';
import cardStyles from '../Card.module.css';
import { addAssigneeToTask, removeAssigneeFromTask } from '../../taskView/TaskViewPage/redux/actions';
import { ICON_MAP } from '../../map/constants';
import { CONTAINERS, pushView } from '../cardNavigationComponent/cardNavigationRedux';
// eslint-disable-next-line import/no-cycle
import AssigneeCard from '../assigneeCard/AssigneeCard';
import { getAvailabilityLabelForUserAndSelectedDate } from '../../../HelperMethods/UserUtilities';
import { useGetUsersOfUserSideBar } from '../hooks';
import { availableAssigneeForTheTask, isUnassignAllowed } from '../../../HelperMethods/TaskUtilities';
import { DD_MM_YYYY_FORMAT } from '../../../HelperMethods/constants';

const useGetTask = (taskId) => {
  const tasksInReducer = useSelector((state) => state.taskViewPageReducer.tasks.items);
  let task = useSelector((state) => state.taskViewPageReducer.parentChildTasks.items.find((t) => t.id === taskId));
  if (!task) {
    task = tasksInReducer.find((t) => t.id === taskId);
  }
  return task;
};

const useGetAssigneesAvailable = (task) => {
  let users = useGetUsersOfUserSideBar();
  if (task?.taskType?.needEquipment) {
    users = users.filter((user) => user?.isEquipmentResource);
  }
  const { areasForDropdown: areas } = useSelector((state) => state.appReducer);
  const { taskTypes } = useSelector((state) => state.taskTypeReducer);
  const assignees = useMemo(() => {
    const assigneeOfTask = availableAssigneeForTheTask({ areas, task, taskTypes, users });
    return assigneeOfTask;
  }, [areas, task, taskTypes, users]);
  return assignees;
};

const TaskAssigneeCard = React.memo(function TaskAssigneeCard(props) {
  const { taskId, container, readOnly = false, pushTaskCard } = props;
  const task = useGetTask(taskId);
  const assignees = useGetAssigneesAvailable(task);
  const [searchText, setSearchText] = useState('');
  const searchTextRefinedUsingRegex = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').toLowerCase();
  const filteredAssignees = assignees.filter((a) => {
    if (searchTextRefinedUsingRegex.trim() === '') return true;
    const fullName = `${a.firstName} ${a.lastName}`.toLowerCase();
    return fullName.includes(searchTextRefinedUsingRegex);
  });
  if (!task) return null;
  const assetType = _.get(task, 'asset.type', 'well');
  const canUnassign = isUnassignAllowed(task);

  return (
    <div className={cardStyles.card}>
      <div className={cardStyles.cardHeader} style={{ paddingBottom: '5px', alignItems: 'flex-end' }}>
        <div className={cardStyles.taskHeaderLines}>
          <div className={cardStyles.mainHeader}>
            {`${_.get(task, 'taskType.name', '')} at ${_.get(task, 'asset.stop.name', '')}`}
          </div>
          <div
            className={cardStyles.subHeader}
            onClick={
              pushTaskCard
                ? () => {
                    pushTaskCard();
                  }
                : null
            }
            style={pushTaskCard ? { cursor: 'pointer', textDecoration: 'underLine' } : {}}
          >
            {_.get(task, 'taskNumber', '')}
          </div>
          <div className={cardStyles.subHeader}>
            <span className={cardStyles.subHeaderIcon}>
              <img src={ICON_MAP[assetType]} alt={assetType} style={{ width: '12px' }} />
            </span>
            {task.asset.name}
          </div>
        </div>
      </div>
      <div className={cardStyles.cardContent}>
        <SearchBar
          onSearchTextChange={(text) => {
            setSearchText(text);
          }}
          searchText={searchText}
        />
        <div style={{ padding: '5px 16px' }}>Availability on {moment(task.startTime).format(DD_MM_YYYY_FORMAT)}</div>
        <div style={{ padding: '0 16px' }}>
          {filteredAssignees.map((a) => (
            <AssigneeBox
              container={container}
              key={a.id}
              assignee={a}
              task={task}
              canUnassign={canUnassign}
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

function SearchBar({ searchText, onSearchTextChange }) {
  return (
    <div className={styles.searchBarContainer}>
      <Input
        value={searchText}
        onChange={(e) => {
          onSearchTextChange(e.target.value);
        }}
        style={{ width: '100%' }}
        placeholder="Search..."
        icon="search"
      />
    </div>
  );
}

SearchBar.propTypes = {
  searchText: PropTypes.string.isRequired,
  onSearchTextChange: PropTypes.func.isRequired,
};

function AssigneeBox({ assignee, task, container, canUnassign, readOnly }) {
  const dispatch = useDispatch();
  const isUserAssignedToTask = task.assigneeIds
    .filter((a) => !a.isDeleted)
    .map((a) => a.assigneeId)
    .includes(assignee.id);
  return (
    <div className={styles.assigneeBox}>
      <div className={styles.assigneeDetails}>
        <div
          className={styles.assigneeName}
          onClick={() => {
            dispatch(pushView(container, { component: AssigneeCard, props: { assigneeId: assignee.id } }));
          }}
        >
          {`${assignee.firstName} ${assignee.lastName}`}
        </div>
        <div style={{ fontSize: '12px' }}>
          {getAvailabilityLabelForUserAndSelectedDate(assignee, moment(task.startTime).format('YYYY-MM-DD'))}
        </div>
      </div>
      <div className={styles.actionButtonsContainer}>
        {isUserAssignedToTask ? (
          <>
            {canUnassign && (
              <Button
                className={styles.actionButtons}
                type="button"
                onClick={() => dispatch(removeAssigneeFromTask(task, assignee))}
                disabled={readOnly}
              >
                <div className={styles.rotate45Deg}>+</div>&nbsp;&nbsp;Cancel
              </Button>
            )}
            &nbsp;&nbsp;&nbsp;&nbsp;
            <div className={readOnly ? styles.assignedBoxDisabled : styles.assignedBox} disabled={readOnly}>
              <i className="fa fa-check" />
            </div>
          </>
        ) : (
          <Button
            className={styles.actionButtons}
            style={{ paddingRight: '0px', textAlign: 'right' }}
            type="button"
            onClick={() => dispatch(addAssigneeToTask(task, assignee))}
            disabled={readOnly}
          >
            +&nbsp;&nbsp;Assign
          </Button>
        )}
      </div>
    </div>
  );
}

AssigneeBox.propTypes = {
  task: PropTypes.shape({
    assigneeIds: PropTypes.array,
    startTime: PropTypes.string,
  }).isRequired,
  container: PropTypes.oneOf(Object.values(CONTAINERS)).isRequired,
  assignee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
  }).isRequired,
  canUnassign: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
};

TaskAssigneeCard.propTypes = {
  taskId: PropTypes.string.isRequired,
  container: PropTypes.oneOf(Object.values(CONTAINERS)).isRequired,
  readOnly: PropTypes.bool,
  pushTaskCard: PropTypes.func,
};

export default TaskAssigneeCard;
